import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Keypad from './Keypad';
import { amountKeyFromEvent, applyAmountKey } from './amount';
import { CUSTOM_DEFAULT_NAME, formatCents } from './money';
import { COLORS } from './theme';

type Props = {
  /** Called with the entered price in cents and the (possibly empty)
   *  name. The caller applies the default-name fallback. */
  onAdd: (name: string, priceCents: number) => void;
  /** True while any dialog is open — suppresses the global key handler
   *  so the panel doesn't eat keystrokes meant for a modal. */
  suspended: boolean;
};

/** Custom Amount entry — the industry term (and Square's) for ringing
 *  up something that isn't in the catalog. */
const CustomItemPanel: React.FC<Props> = ({ onAdd, suspended }) => {
  const [cents, setCents] = useState(0);
  const [name, setName] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  const canAdd = cents > 0;

  const submit = () => {
    if (!canAdd) return;
    onAdd(name, cents);
    setCents(0);
    setName('');
  };

  // Keyboard routing. The rule:
  //
  //   * Number-pad keys ALWAYS drive the price, even while the name
  //     field has focus — that's what lets a cashier type a name and
  //     then punch in an amount without reaching for the mouse.
  //   * Top-row digits go to whatever has focus, so they type into the
  //     name field normally and only fall through to the price when
  //     the cashier isn't in a text box.
  //
  // `e.code` is what makes this possible: `key` is "1" for both
  // Numpad1 and Digit1, `code` tells them apart.
  useEffect(() => {
    if (suspended) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const inTextField = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';

      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
        return;
      }

      const key = amountKeyFromEvent(e, inTextField);
      if (key === null) return;
      // Escape with an empty amount should fall through to the name
      // field rather than doing nothing visible.
      if (key === 'clear' && inTextField && cents === 0) {
        setName('');
        return;
      }
      e.preventDefault();
      setCents((c) => applyAmountKey(c, key));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: 2,
        pb: 2,
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          backgroundColor: COLORS.surface,
          border: `2px solid ${canAdd ? COLORS.accent : COLORS.border}`,
          borderRadius: 2,
          px: 3,
          py: 2,
          textAlign: 'right',
        }}
      >
        <Typography
          sx={{ color: COLORS.textMuted, letterSpacing: '0.1em', fontSize: '0.8rem' }}
        >
          CUSTOM AMOUNT
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '3.5rem',
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
            color: canAdd ? COLORS.text : COLORS.textMuted,
          }}
          aria-live="polite"
        >
          {formatCents(cents)}
        </Typography>
      </Box>

      <TextField
        fullWidth
        inputRef={nameRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={CUSTOM_DEFAULT_NAME}
        label="Item name (optional)"
        autoComplete="off"
        inputProps={{ maxLength: 80, 'aria-label': 'Custom item name' }}
        InputProps={{ sx: { fontSize: '1.1rem', backgroundColor: COLORS.surface } }}
        helperText="Number-pad keys always go to the price, even while typing here."
      />

      <Keypad onKey={(k) => setCents((c) => applyAmountKey(c, k))} />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setCents(0);
            setName('');
          }}
          disabled={cents === 0 && name === ''}
          sx={{ flex: '0 0 120px', py: 2, fontWeight: 700, color: COLORS.text }}
        >
          CLEAR
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canAdd}
          startIcon={<AddIcon />}
          sx={{ flex: 1, py: 2, fontSize: '1.15rem', fontWeight: 700 }}
        >
          ADD TO SALE
        </Button>
      </Box>
    </Box>
  );
};

export default CustomItemPanel;
